import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { api, ApiError, type RequestOptions } from '@/lib/api-client';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore } from '@/store/ui.store';

/**
 * Every query is keyed by the active company, so switching companies can never
 * show another company's cached figures.
 */
export function useApiQuery<T>(
  key: unknown[],
  path: string,
  query?: RequestOptions['query'],
  options?: Partial<UseQueryOptions<T, ApiError>>,
) {
  const companyId = useAuthStore((s) => s.companyId);

  return useQuery<T, ApiError>({
    queryKey: [companyId, ...key, query ?? {}],
    queryFn: () => api.get<T>(path, { query }),
    enabled: Boolean(companyId) && (options?.enabled ?? true),
    ...options,
  });
}

/** Query for endpoints that are not company-scoped (auth, company list). */
export function useGlobalQuery<T>(
  key: unknown[],
  path: string,
  options?: Partial<UseQueryOptions<T, ApiError>>,
) {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: () => api.get<T>(path, { withoutCompany: true }),
    ...options,
  });
}

interface MutationConfig<TBody, TResult> {
  method?: 'post' | 'patch' | 'delete';
  /** Query key prefixes to invalidate on success. */
  invalidate?: string[][];
  successMessage?: string | ((result: TResult) => string);
  onSuccess?: (result: TResult, body: TBody) => void;
  /** Makes a retried submission safe for anything that posts money. */
  idempotent?: boolean;
}

export function useApiMutation<TBody = unknown, TResult = unknown>(
  path: string | ((body: TBody) => string),
  config: MutationConfig<TBody, TResult> = {},
) {
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.companyId);
  const toast = useUiStore((s) => s.toast);

  return useMutation<TResult, ApiError, TBody>({
    mutationFn: (body: TBody) => {
      const url = typeof path === 'function' ? path(body) : path;
      const options: RequestOptions = config.idempotent
        ? { idempotencyKey: crypto.randomUUID() }
        : {};

      if (config.method === 'delete') return api.delete<TResult>(url, options);
      if (config.method === 'patch') return api.patch<TResult>(url, body, options);
      return api.post<TResult>(url, body, options);
    },

    onSuccess(result, body) {
      for (const key of config.invalidate ?? []) {
        void queryClient.invalidateQueries({ queryKey: [companyId, ...key] });
      }
      if (config.successMessage) {
        const message =
          typeof config.successMessage === 'function'
            ? config.successMessage(result)
            : config.successMessage;
        toast({ title: message, variant: 'success' });
      }
      config.onSuccess?.(result, body);
    },

    onError(error) {
      toast({
        title: error.statusCode >= 500 ? 'Something went wrong' : 'Could not complete that',
        description: error.fieldErrors.length > 1 ? error.fieldErrors.join(' · ') : error.message,
        variant: 'error',
      });
    },
  });
}
